/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { FileCheck, CalendarDays, Coins, Search } from "lucide-react";
import RenderAdminPage from "../RenderAdminPage";
import RenderLoanPage from "../../LoanModel/RenderLoanPage";
import PageSkeleton from "../../Components/PageSkeleton";
import axiosInstance from "../../services/Axios/axios";

// Read-only view over the clearance letters issued from the Issue Clearance
// screen. Counters come from /clearance/stats; the table from /clearance, which
// returns a plain array newest-first, so search and paging are client-side.
//
// Nothing was recorded before the clearance module went live, so these numbers
// count letters issued from that point on — not historical ones.

const PAGE_SIZE = 10;

function ClearanceDashboard() {
  const location = useLocation();
  const Shell = location.pathname.startsWith("/admin")
    ? RenderAdminPage
    : RenderLoanPage;

  const [stats, setStats] = useState({ total: 0, this_month: 0, total_amount: 0 });
  const [issued, setIssued] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    axiosInstance
      .get("/clearance/stats")
      .then((res) => setStats(res.data || {}))
      .catch(() => {});

    axiosInstance
      .get("/clearance")
      .then((res) => setIssued(res.data || []))
      .catch(() => setIssued([]))
      .finally(() => setHasLoaded(true));
  }, []);

  useEffect(() => {
    setPage(0);
  }, [search]);

  const filtered = useMemo(() => {
    if (!search) return issued;
    const q = search.toLowerCase();
    return issued.filter(
      (c) =>
        c.member_name?.toLowerCase().includes(q) ||
        c.staff_id?.toLowerCase().includes(q) ||
        c.issued_by?.toLowerCase().includes(q)
    );
  }, [issued, search]);

  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const money = (value) =>
    Number(value || 0).toLocaleString("en-GH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const date = (value) =>
    value ? new Date(value).toLocaleDateString("en-GB") : "—";

  const cards = [
    {
      label: "Total Clearances Issued",
      value: stats.total ?? 0,
      icon: <FileCheck size={20} />,
      tone: "text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/25",
    },
    {
      label: "Issued This Month",
      value: stats.this_month ?? 0,
      icon: <CalendarDays size={20} />,
      tone: "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/25",
    },
    {
      label: "Total Amount Cleared",
      value: `GHS ${money(stats.total_amount)}`,
      icon: <Coins size={20} />,
      tone: "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/25",
    },
  ];

  return (
    <Shell title="Clearance Dashboard">
      <p className="text-sm text-slate-500 dark:text-slate-400 -mt-4 mb-5">
        Clearance letters issued to members who have fully settled a loan.
      </p>

      <div className="grid gap-4 mb-5 grid-cols-1 sm:grid-cols-3">
        {cards.map((card) => (
          <div
            key={card.label}
            className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 px-4 py-4 shadow-sm"
          >
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${card.tone}`}>
              {card.icon}
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {card.label}
              </p>
              <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 truncate">
                {card.value}
              </h3>
            </div>
          </div>
        ))}
      </div>

      <div className="relative w-full sm:w-96 mb-4">
        <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by member, staff ID or issuer"
          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      {!hasLoaded && issued.length === 0 ? (
        <PageSkeleton cards={0} rows={8} cols={6} />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 dark:bg-gray-900/50 border-b border-slate-200 dark:border-gray-700">
                <tr>
                  {["Member", "Staff ID", "Amount Paid", "Settled", "Issued By", "Issued"].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                {pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center">
                      <FileCheck size={30} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                      <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                        {search ? "No matching clearances" : "No clearances issued yet"}
                      </p>
                      {!search && (
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                          Letters appear here as they are issued.
                        </p>
                      )}
                    </td>
                  </tr>
                ) : (
                  pageItems.map((c) => (
                    <tr key={c._id} className="hover:bg-slate-50 dark:hover:bg-gray-700/40 transition-colors">
                      <td className="px-4 py-3 text-xs font-bold text-slate-800 dark:text-slate-100">
                        {c.member_title ? `${c.member_title} ` : ""}
                        {c.member_name || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">
                        {c.staff_id || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs font-bold text-emerald-700 dark:text-emerald-400 whitespace-nowrap">
                        GHS {money(c.amount_paid)}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        {date(c.settled_date)}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">
                        {c.issued_by || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
                        {date(c.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-gray-700">
              <p className="text-xs text-slate-400 dark:text-slate-500">
                Page {page + 1} of {pageCount} · {filtered.length} total
              </p>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-gray-700"
                >
                  Prev
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={page >= pageCount - 1}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Shell>
  );
}

export default ClearanceDashboard;
